import Link from "next/link";
import { useFestivalExperience } from "./FestivalExperienceContext";
import { festivalThemeStyle } from "@/commons/festivalExperience/designPresets";
import { DiyaIcon } from "./FestivalOrnaments";
import FestivalPoojaCard from "./FestivalPoojaCard";
import { poojaHeadingOf } from "./FestivalHero";
import { PhoneCall } from "lucide-react";
import { LEAD_CTAS, LeadTag, openFestivalCallback } from "./leadCtas";

// Literal class names so Tailwind can see them; grid width follows the mapping size.
const MD_COLS = { 1: "md:grid-cols-2", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5" };

// Layer 2 on listing/category pages (PRD §4). A short, self-contained rail
// above the normal results — it never touches filters, sort order, query
// params or the result grid. Hidden when nothing is bookable in this city.
const FestivalCollection = ({ slotKey = "listing.collection" }) => {
  const { experience } = useFestivalExperience();
  const poojas = experience?.poojas?.slice(0, 4) || [];
  if (!experience?.primaryCampaign || !poojas.length) return null;

  const { copy, primaryCampaign, activeDay } = experience;
  const subtitle = activeDay ? `${activeDay.label} · ${activeDay.name}` : copy.eyebrow || primaryCampaign.name;

  return (
    <section
      style={festivalThemeStyle(primaryCampaign.themePreset, activeDay?.color)}
      data-fx-impression={slotKey}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[hsl(var(--fx-bg))] to-[hsl(355_62%_24%)] p-4 md:p-5 text-[hsl(var(--fx-fg))]"
    >
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[hsl(var(--fx-bg-2)/0.6)] blur-3xl" />
      <div className="relative mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <DiyaIcon className="h-6 w-6 shrink-0" />
          <div className="min-w-0">
            <h2 className="truncate font-playfair text-lg md:text-xl font-bold !text-[hsl(var(--fx-fg))]">{poojaHeadingOf(experience)}</h2>
            <p className="truncate font-sans text-xs text-[hsl(var(--fx-muted))]">{subtitle}</p>
          </div>
        </div>
        <Link
          href={primaryCampaign.landingHref}
          data-fx-slot={slotKey}
          className="shrink-0 rounded-full bg-[hsl(var(--fx-accent))] px-3.5 py-1.5 font-sans text-xs font-bold text-[hsl(var(--fx-accent-fg))]"
        >
          See all
        </Link>
      </div>
      <div className={`relative -mx-4 flex snap-x gap-3 overflow-x-auto scrollbar-hide px-4 md:mx-0 md:grid ${MD_COLS[poojas.length + (LEAD_CTAS.railCallbackCard ? 1 : 0)] || "md:grid-cols-4"} md:px-0`}>
        {poojas.map((p, i) => (
          <div key={`${p.slug}-${p.linkLocation || ""}-${p.linkLanguage || ""}`} className="w-[62vw] max-w-[240px] shrink-0 snap-start md:w-auto md:max-w-none">
            <FestivalPoojaCard pooja={p} slotKey={slotKey} rank={i + 1} compact />
          </div>
        ))}
        {LEAD_CTAS.railCallbackCard && (
          <div className="w-[62vw] max-w-[240px] shrink-0 snap-start md:w-auto md:max-w-none">
            <button
              type="button"
              onClick={() => openFestivalCallback({ slot: `${slotKey}.callback` })}
              className="relative flex h-full min-h-[180px] w-full flex-col items-start justify-between gap-3 rounded-2xl border border-dashed border-[hsl(var(--fx-accent)/0.7)] bg-[hsl(var(--fx-accent)/0.1)] p-4 text-left transition-colors hover:bg-[hsl(var(--fx-accent)/0.18)]"
            >
              <LeadTag n={6} />
              <PhoneCall className="h-7 w-7 text-[hsl(var(--fx-accent))]" />
              <span>
                <span className="block font-playfair text-base font-bold leading-snug text-[hsl(var(--fx-fg))]">Can&apos;t decide?</span>
                <span className="mt-1 block font-sans text-xs text-[hsl(var(--fx-muted))]">A pandit will call you in 10 min and suggest the right puja.</span>
              </span>
              <span className="rounded-full bg-[hsl(var(--fx-accent))] px-3 py-1.5 font-sans text-xs font-bold text-[hsl(var(--fx-accent-fg))]">Get a call back</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FestivalCollection;
